import React, { useEffect, useState } from "react";
import RightArrow from "../../../../assets/Icons/Analytics/blackRightArrow.svg";
import ProfileIcon from "../../../../assets/Icons/profileMenu/profile.svg";
import DefaultEventBanner from "../../../../assets/Images/socha_banner.png";
import { ClipboardList, Eye, Mars, NonBinary, Venus } from "lucide-react";
import RightSideDrawer from "../../../../components/rightDrawer";
import GuestAnswersView from "../../../../components/GuestAnswersView";
import { eventDetailService } from "../../../../services/eventDetailServices";
import { useSpinner } from "../../../../components/spinner/SpinnerContext";
import UserProfile from "../../../../screens/profile/userProfile";
import { useLocation, useNavigate } from "react-router-dom";
import {
  convertUtcToLocal,
  formatDateTime,
  getDayOfWeek,
  getMonthAndDay,
  getTime,
  isSameDay,
} from "../../../../utils/common";
import { toast } from "react-toastify";
import ConfirmationAlert from "./../../../../customComponents/ConfirmationAlert";
import { getLocalStorage } from "../../../../utils/localStorage";
import { LOCAL_STORAGE } from "../../../../utils/constants";

export default function PendingGuestList({ eventName, bannerImage }: any) {
  const { setShowSpinner } = useSpinner();
  const navigate = useNavigate();
  const location = useLocation();
  //   const { eventData, eventStartDateTime, eventEndDateTime } = location.state;
  const queryParams = new URLSearchParams(location.search);
  const eventId: any = queryParams.get("eventid");
  const hostId: any = queryParams.get("hostid");
  const userId: any = getLocalStorage(LOCAL_STORAGE.USER_ID);
  const [eventDetails, setEventDetails] = useState<any>([]);
  const [guests, setGuests] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState("");
  const [eventStartDateTime, setEventStartDateTime] = useState("");
  const [eventEndDateTime, setEventEndDateTime] = useState("");

  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);

  const [actionType, setActionType] = useState<"approve" | "reject" | null>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [guestPage, setGuestPage] = useState(1);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [showGuestAnswersDrawer, setShowGuestAnswersDrawer] = useState(false);
  const [answersGuestId, setAnswersGuestId] = useState<number | null>(null);
  const [answersGuestName, setAnswersGuestName] = useState("");

  useEffect(() => {
    if (!eventId || !hostId) {
      toast.error(
        "Invalid event or host details. Please access this page from a valid event."
      );
      return
    //   navigate(-1); // or navigate("/events") if you prefer a safe route
    }
  }, []);

  useEffect(() => {
    if (eventId && hostId) {
      getHostEventDtls(eventId, hostId);
    }
  }, [eventId, hostId]);

  useEffect(() => {
    if (eventId && hostId) {
    fetchApprovalGuestList(1);
    }
  }, [eventId, hostId]);

  useEffect(() => {
    setEventStartDateTime(
      convertUtcToLocal(eventDetails?.startTime, eventDetails?.timezone)
    );
    setEventEndDateTime(
      convertUtcToLocal(eventDetails?.endTime, eventDetails?.timezone)
    );
  }, [eventDetails]);

  const getHostEventDtls = async (eventId?: number, hostId?: number) => {
    try {
      setShowSpinner(true);
      const res = await eventDetailService.getHostEventDetails({
        eventId,
        userId,
      });
      const eventData = res?.data?.events;
      if (res?.status) {
        setEventDetails(res?.data?.events);
        setEventStartDateTime(
          convertUtcToLocal(eventData?.startTime, eventData?.timezone)
        );
        setEventEndDateTime(
          convertUtcToLocal(eventData?.endTime, eventData?.timezone)
        );
        setHasQuestions(!!res?.data?.hasQuestions);
        setShowSpinner(false);
      }
    } catch (error: any) {
      console.error(
        "Error in getGuestEventDtls:",
        error?.response?.data?.error || error?.message
      );
      setShowSpinner(false);
    } finally {
      setShowSpinner(false);
    }
  };

  const fetchApprovalGuestList = async (page = 1) => {
    try {
      setShowSpinner(true);

      const response = await eventDetailService.getPendingRsvpGuests({
        eventId: eventId,
        hostId: hostId,
        pageNo: page,
        pageSize: 20,
        search: searchKey,
      });

      if (response?.data?.users) {
        setGuests(
          response.data.users.map((u: any) => ({
            id: u.userId,
            name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
            firstName: u.firstName,
            lastName: u.lastName,
            profilePhoto: u.profilePhoto,
            joiningStatus: u.joiningStatus,
            email: u.email,
            phoneNo: u.phoneNo,
            gender: u.gender,
            createdAt: u.createdAt,
          }))
        );
        setTotalPages(response?.data?.totalPages || 1);
        setCurrentPage(page);
        setGuestPage(response?.data?.currentPage || 1);
      }
    } finally {
      setShowSpinner(false);
    }
  };

  const handleViewProfile = (user: any) => {
    setSelectedUser(user);
    setIsConnectionModalOpen(true);
  };

  const handleApprove = async (guestId?: any) => {
    try {
      setShowSpinner(true);
      const eventId = eventDetails?.eventId;
      const hostId = eventDetails?.hostUserId;
      const res = await eventDetailService.approveRSVP({
        hostId: Number(hostId),
        eventId: Number(eventId),
        userId: Number(guestId),
        status: "Going",
      });
      if (res.status) {
        toast.success("You have approved this guest's request");
        fetchApprovalGuestList(guestPage);
      }
      setShowSpinner(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.error || "Failed to approve");
      setShowSpinner(false);
    }
  };

  const handleReject = async (guestId: number) => {
    try {
      setShowSpinner(true);
      const eventId = eventDetails?.eventId;
      const hostId = eventDetails?.hostUserId;
      const res = await eventDetailService.rejectRSVP({
        hostId: Number(hostId),
        eventId: Number(eventId),
        userId: Number(guestId),
      });
      if (res.status) {
        toast.success("You have rejected this guest's request");
        fetchApprovalGuestList(guestPage);
      }
      setShowSpinner(false);
    } catch (err: any) {
          toast.error(
            err?.response?.data?.error || err.error || "Failed to reject",
          );
      setShowSpinner(false);
    }
  };

  const getGenderColorStyles = (gender: string) => {
    if (["Man", "man", "Male", "Men"].includes(gender)) {
      return {
        bg: "#bda1ff",
        border: "#bda1ff",
        text: "#5E3BC1",
      };
    } else if (["Woman", "woman", "Female", "Women"].includes(gender)) {
      return {
        bg: "#feb89a",
        border: "#feb89a",
        text: "#C2410C",
      };
    } else {
      return {
        bg: "#fffaa2",
        border: "#fffaa2",
        text: "#855C00",
      };
    }
  };

  const getGenderIcon = (
    genderInfo: string,
    className = "w-6 h-6",
    strokeColor: string
  ) => {
    const { bg, border, text } = getGenderColorStyles(genderInfo);
    if (["Man", "man", "Male", "Men", "Men"].includes(genderInfo)) {
      return (
        <Mars
          className={`${className}`}
          stroke={text}
          strokeWidth={2}
          // size={20}
        />
      );
    } else if (["Woman", "woman", "Female", "Women"].includes(genderInfo)) {
      return (
        <Venus
          className={`${className}`}
          stroke={text}
          strokeWidth={2}
          // size={20}
        />
      );
    } else {
      return (
        <NonBinary
          className={`${className}`}
          stroke={text}
          strokeWidth={2}
          // size={15}
        />
      );
    }
  };

  const getGenderIconLabel = (
    genderInfo: string,
    className = "w-6 h-6 pl-2"
  ) => {
    if (["Man", "man", "Male", "Men"].includes(genderInfo)) {
      return "Man";
    } else if (["Woman", "woman", "Female", "Women"].includes(genderInfo)) {
      return "Woman";
    } else {
      return "Non-Binary";
    }
  };

  return (
    <div className="w-full  flex flex-col px-4 md:px-8 py-4">
      {/* ================= HEADER ================= */}
      <div className="mb-6">
        <div
          className="flex items-center gap-2 cursor-pointer mb-2"
          onClick={() => navigate(-1)}
        >
          <img src={RightArrow} className="w-5 h-5 rotate-180" />
          <span className="text-base font-semibold text-gray-800">
            Back to Event
          </span>
        </div>

        <p className="text-sm text-gray-600">
          Review guests who have requested to attend your event
        </p>
      </div>

      {/* ================= EVENT INFO + SEARCH ================= */}

      {eventDetails && eventId && hostId ?<div className="w-full">
        <div className="flex flex-col md:flex-row md:items-center xl:justify-between gap-6 mb-6 items-center">
          {/* EVENT INFO */}
          {eventDetails?.eventName && 
          
          <><div className="flex gap-4 bg-[#FAF2F6] p-4 rounded-2xl w-full xl:max-w-[50%]">
            <img
              src={eventDetails?.bannerImage || DefaultEventBanner}
              className="w-20 h-20 rounded-xl object-cover shrink-0"
            />

            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {eventDetails?.eventName}
              </h2>

              <p className="text-[10px] md:text-xs text-gray-600">
                <div className="flex md:gap-1 items-center">
                  <span>
                    {getDayOfWeek(eventStartDateTime) +
                      " " +
                      getMonthAndDay(eventStartDateTime)}
                  </span>
                  <span className="mx-1">-</span>
                  <span className="text-[#0F090B]">
                    {getTime(eventStartDateTime)}
                  </span>
                  <span className="mx-1 text-[#786469]">to</span>
                  {isSameDay(eventStartDateTime, eventEndDateTime) ? (
                    <span className="text-[#0F090B]">
                      {getTime(eventEndDateTime)}
                    </span>
                  ) : (
                    <div className="flex md:gap-1">
                      <span>
                        {getDayOfWeek(eventEndDateTime) +
                          " " +
                          getMonthAndDay(eventEndDateTime)}
                      </span>
                      <span className="mx-1">-</span>
                      <span className="text-[#0F090B]">
                        {getTime(eventEndDateTime)}
                      </span>
                    </div>
                  )}
                </div>
              </p>

              {eventDetails?.location && (
                <p className="text-[10px] md:text-xs text-gray-700 mt-1 flex items-start gap-1 truncate min-w-0">
                  <span>📍</span>
                  <span className="line-clamp-2">{eventDetails?.location}</span>
                </p>
              )}
            </div>
          </div>

          {/* SEARCH */}
          {guests.length > 0 &&<div className="flex gap-2 w-full xl:max-w-[320px]">
            <input
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              placeholder="Search guest"
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={() => fetchApprovalGuestList(1)}
              className="bg-black text-white px-4 py-2 rounded-lg text-sm"
            >
              Search
            </button>
          </div>}
          </>}
        </div>

        {/* ================= CONTENT ================= */}
        <div className="flex-1 overflow-y-auto">
          {/* DESKTOP TABLE */}
          <div className="hidden md:block">
            {guests.length > 0 ? (
              <table className="w-full border-separate border-spacing-y-2">
                <thead>
                  <tr className="text-sm text-gray-500 text-left">
                    <th className="px-4">User</th>
                    <th className="px-4">Gender</th>
                    <th className="px-4">Email</th>
                    <th className="px-4">Requested On</th>
                    <th className="px-4 text-center">View</th>
                    {hasQuestions && <th className="px-4 text-center">Answers</th>}
                    <th className="px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.length > 0 ? (
                    guests.map((row) => (
                      <tr
                        key={row.id}
                        className="bg-[#FAF2F6] hover:bg-[#F2DDE2]"
                      >
                        <td className="px-4 py-3 flex items-center gap-3">
                          <img
                            src={row.profilePhoto || ProfileIcon}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <span className="font-medium text-gray-800">
                            {row.name}
                          </span>
                        </td>
                        <td className="py-4 text-xs px-4 align-middle">
                          <div className="flex items-center ">
                            {" "}
                            {getGenderIcon(row?.gender, "w-5", "")}{" "}
                            {getGenderIconLabel(row?.gender, "w-6 h-6 pl-2")}
                          </div>
                        </td>
                        <td className="px-4 text-gray-700">{row.email}</td>
                        <td className="px-4 text-gray-700">{formatDateTime(row.createdAt)}</td>
                        <td className="px-4 text-gray-700 text-center">
                          {" "}
                          <button
                            className="px-3 py-1 bg-purple-600 text-white hover:bg-purple-800 transition-colors rounded-lg text-xs "
                            onClick={() => handleViewProfile(row)}
                          >
                            View Profile
                          </button>
                        </td>
                        {hasQuestions && (
                          <td className="px-4 text-center">
                            <button
                              className="flex items-center justify-center mx-auto bg-[#FAF2F6] hover:bg-[#F1E7EC] rounded-lg p-1 transition-colors"
                              title="View Submitted Answers"
                              onClick={() => {
                                setAnswersGuestId(row.id);
                                setAnswersGuestName(row.name);
                                setShowGuestAnswersDrawer(true);
                              }}
                            >
                              <ClipboardList size={18} className="text-[#0F090B]" />
                            </button>
                          </td>
                        )}
                        <td className="px-4 text-gray-700 text-center">
                          {/* <Eye
                        className="w-6 h-6 cursor-pointer text-purple-600 hover:text-purple-800 transition-colors"
                        onClick={() => handleViewProfile(row)}
                      /> */}
                          <div className="flex gap-2 justify-center">
                            <button
                              className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs"
                              onClick={() => {
                                setSelectedUser(row);
                                setActionType("approve");
                              }}
                            >
                              Approve
                            </button>

                            <button
                              className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs"
                              onClick={() => {
                                setSelectedUser(row);
                                setActionType("reject");
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="text-center py-6 text-gray-600"
                      >
                        No guests found!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <p className="text-center py-6 text-gray-600 w-full">
                No guests found!
              </p>
            )}
          </div>

          {/* MOBILE CARDS */}
          {/* MOBILE CARDS */}
          <div className="md:hidden flex flex-col gap-4 mb-3">
            {guests.length > 0 ? (
              guests.map((row) => (
                <div
                  key={row.id}
                  className="bg-white rounded-2xl p-4 shadow-sm"
                >
                  {/* User Info */}
                  <div className="flex items-center gap-3">
                    <img
                      src={row.profilePhoto || ProfileIcon}
                      className="w-12 h-12 rounded-full object-cover"
                    />

                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{row.name}</p>
                      <p className="text-xs text-gray-500">{row.email}</p>
                      <p className="text-xs text-gray-500">{formatDateTime(row.createdAt)}</p>
                    </div>

                    {/* View */}
                    <div className="flex items-center gap-2">
                      <Eye
                        className="w-6 h-6 cursor-pointer text-indigo-600 hover:text-indigo-800"
                        onClick={() => handleViewProfile(row)}
                      />
                      {hasQuestions && (
                        <ClipboardList
                          className="w-5 h-5 cursor-pointer text-[#0F090B] bg-[#FAF2F6] hover:bg-[#F1E7EC] rounded-md p-0.5 transition-colors"
                          onClick={() => {
                            setAnswersGuestId(row.id);
                            setAnswersGuestName(row.name);
                            setShowGuestAnswersDrawer(true);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      className="flex-1 py-2 text-sm rounded-xl bg-green-600 text-white font-medium active:scale-95 transition"
                      onClick={() => {
                        setSelectedUser(row);
                        setActionType("approve");
                      }}
                    >
                      Approve
                    </button>

                    <button
                      className="flex-1 py-2 text-sm rounded-xl bg-red-600 text-white font-medium active:scale-95 transition"
                      onClick={() => {
                        setSelectedUser(row);
                        setActionType("reject");
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-6 text-gray-600 w-full">
                No guests found!
              </p>
            )}
          </div>
        </div>

        {/* ================= PAGINATION ================= */}
        {guests.length > 0 && totalPages > 0 && (
          <div className="border-t bg-white py-3 px-4 flex justify-between items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => fetchApprovalGuestList(currentPage - 1)}
              className="px-3 py-1 bg-black text-white rounded disabled:bg-gray-300"
            >
              Prev
            </button>

            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => fetchApprovalGuestList(currentPage + 1)}
              className="px-3 py-1 bg-black text-white rounded disabled:bg-gray-300"
            >
              Next
            </button>
          </div>
        )}

        {/* ================= USER PROFILE DRAWER ================= */}
        {isConnectionModalOpen && selectedUser && (
          <RightSideDrawer
            open
            onClose={() => setIsConnectionModalOpen(false)}
            title="Guest Profile"
            paymentDrawer
            className="md:w-[65vw] lg:w-[55vw] side-drawer-connection-bg"
          >
            <UserProfile
              eventId={eventDetails?.eventId}
              hostId={eventDetails?.hostUserId}
              selectedUserId={selectedUser.id}
              userObj={selectedUser}
              isHost={true}
            />
          </RightSideDrawer>
        )}

        <ConfirmationAlert
          open={!!actionType}
          onClose={() => {
            setActionType(null);
            setSelectedUser(null);
          }}
          title={`${actionType === "approve" ? "Approve" : "Reject"} Guest`}
          message={`Are you sure you want to ${actionType} ${selectedUser?.firstName} ${selectedUser?.lastName}?`}
          onConfirm={() => {
            if (actionType === "approve") {
              handleApprove(selectedUser?.id);
            } else {
              handleReject(Number(selectedUser?.id));
            }
            setActionType(null);
            setSelectedUser(null);
          }}
        />

        {/* ================= GUEST ANSWERS DRAWER ================= */}
        {showGuestAnswersDrawer && answersGuestId && (
          <RightSideDrawer
            open={showGuestAnswersDrawer}
            onClose={() => setShowGuestAnswersDrawer(false)}
            title="Guest Responses"
            paymentDrawer={true}
            flexContent={true}
            className="md:w-[50vw] lg:w-[35vw] side-drawer-connection-bg"
          >
            <div className="flex flex-col flex-1 min-h-0">
              {answersGuestName && (
                <p className="text-base font-semibold text-[#0F090B] mb-3 shrink-0">
                  {answersGuestName}'s Responses
                </p>
              )}
              <GuestAnswersView
                eventId={Number(eventId)}
                guestId={Number(answersGuestId)}
                isHost={true}
              />
            </div>
          </RightSideDrawer>
        )}
      </div>:  <p className="text-center py-6 text-gray-600 w-full">
              No guests found!
            </p>}
    </div>
  );
}
